import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Row,
  Col,
  Spinner,
  Form,
  Alert,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  getFirestore,
  query,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  FaGraduationCap,
  FaUsers,
  FaBook,
  FaChartLine,
  FaSync,
  FaClock,
  FaUserGraduate,
  FaTrophy,
  FaHistory,
} from "react-icons/fa";
import {
  BsCheckCircleFill,
  BsClockFill,
} from "react-icons/bs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];

const DashboardPage = ({ usuario }) => {
  const [cursos, setCursos] = useState([]);
  const [progresos, setProgresos] = useState([]);
  const [respuestas, setRespuestas] = useState([]);
  const [documentosCapacitacion, setDocumentosCapacitacion] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [totalUsuariosEmpresa, setTotalUsuariosEmpresa] = useState(0);
  const [mesSeleccionado, setMesSeleccionado] = useState(() => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${d.getFullYear()}-${m}`;
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (usuario?.ruc) {
      cargarDatos();
    }
  }, [usuario]);
  
  const cargarDatos = async () => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";
  
      // Cargar programas
      const cursosRef = collection(db, `in_capacitacion/${rucEmpresa}/programas`);
      const cursosSnapshot = await getDocs(cursosRef);
      const cursosData = [];
  
      cursosSnapshot.forEach((doc) => {
        const data = doc.data();
        cursosData.push({
          id: doc.id,
          titulo: data.nombre || "Sin título",
          categoria: data.categoria || "General",
          modulos: data.modulos || [],
          duracionHoras: data.duracionHoras || 1,
          fechaCreacion: data.fechaCreacion,
        });
      });
  
      // Cargar progresos
      const progresosRef = collection(db, `in_capacitacion/${rucEmpresa}/progresos`);
      const progresosSnapshot = await getDocs(progresosRef);
      const progresosData = [];
  
      progresosSnapshot.forEach((doc) => {
        const data = doc.data();
        progresosData.push({
          dni: doc.id,
          modulosCompletados: Array.isArray(data.modulosCompletados)
            ? data.modulosCompletados
            : [],
        });
      });
  
      // Cargar respuestas
      const respuestasRef = collection(db, `in_capacitacion/${rucEmpresa}/respuestas`);
      const respuestasSnapshot = await getDocs(respuestasRef);
      const respuestasData = [];
  
      respuestasSnapshot.forEach((doc) => {
        const data = doc.data();
        respuestasData.push({
          ...data,
          id: doc.id,
        });
      });

      // Cargar documentos de capacitación (asistencia/certificado)
      const docsCapRef = collection(db, `in_capacitacion/${rucEmpresa}/documentos_capacitacion`);
      const docsCapSnapshot = await getDocs(docsCapRef);
      const docsCapData = [];
      docsCapSnapshot.forEach((doc) => {
        const data = doc.data();
        docsCapData.push({ ...data, id: doc.id });
      });

      const asignacionesRef = collection(db, `in_capacitacion/${rucEmpresa}/asignaciones`);
      const asignacionesSnap = await getDocs(asignacionesRef);
      const asignacionesData = [];
      asignacionesSnap.forEach((doc) => {
        asignacionesData.push({ id: doc.id, ...doc.data() });
      });

      const usuariosRef = query(
        collection(db, "users"),
        where("rucEmpresa", "==", rucEmpresa)
      );
      const usuariosSnap = await getDocs(usuariosRef);
  
      // Calcular estadísticas por curso
      cursosData.forEach((curso) => {
        const usuariosInscritos = new Set();
        const usuariosCompletados = new Set();
  
        progresosData.forEach((progreso) => {
          if (!Array.isArray(progreso.modulosCompletados)) return;
          const modulosDelCurso = progreso.modulosCompletados.filter(m => m.startsWith(`${curso.id}_`));
          
          if (modulosDelCurso.length > 0) {
            usuariosInscritos.add(progreso.dni);
            if (modulosDelCurso.length === curso.modulos.length) {
              usuariosCompletados.add(progreso.dni);
            }
          }
        });

        // Señales adicionales de participación por curso
        respuestasData.forEach((r) => {
          if (r?.cursoId === curso.id && r?.dni) {
            usuariosInscritos.add(String(r.dni));
          }
        });
        docsCapData.forEach((d) => {
          if (d?.cursoId === curso.id && d?.dni && d?.visibleParaAdmin !== false) {
            usuariosInscritos.add(String(d.dni));
            if (d.tipoDocumento === "certificado" && d.url) {
              usuariosCompletados.add(String(d.dni));
            }
          }
        });
  
        curso.total_inscritos = usuariosInscritos.size;
        curso.completados = usuariosCompletados.size;
        curso.engagement = curso.total_inscritos > 0 ? (curso.completados / curso.total_inscritos) * 100 : 0;
      });
  
      setCursos(cursosData);
      setProgresos(progresosData);
      setRespuestas(respuestasData);
      setDocumentosCapacitacion(docsCapData);
      setAsignaciones(asignacionesData);
      setTotalUsuariosEmpresa(usuariosSnap.size);
    } catch (error) {
      console.error("Error al cargar datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const calcularEstadisticas = () => {
    const [anioSel, mesSel] = mesSeleccionado.split("-");
    const usuariosCapacitadosMes = new Set();
    const usuariosCapacitadosTotal = new Set();
    const esDelMesSeleccionado = (valorFecha) => {
      if (!valorFecha) return false;
      const fecha = valorFecha?.seconds
        ? new Date(valorFecha.seconds * 1000)
        : new Date(valorFecha);
      if (Number.isNaN(fecha.getTime())) return false;
      return (
        String(fecha.getFullYear()) === anioSel &&
        String(fecha.getMonth() + 1).padStart(2, "0") === mesSel
      );
    };

    cursos.forEach(curso => {
      documentosCapacitacion.forEach((d) => {
        if (d?.cursoId === curso.id && d?.dni && d?.visibleParaAdmin !== false) {
          if (d.tipoDocumento === "certificado" && d.url) {
            usuariosCapacitadosTotal.add(String(d.dni));
          }
          if (
            d.tipoDocumento === "certificado" &&
            d.url &&
            (esDelMesSeleccionado(d.actualizadoEn) || esDelMesSeleccionado(d.generadoEn))
          ) {
            usuariosCapacitadosMes.add(String(d.dni));
          }
        }
      });
    });

    const categoriasCount = {};
    cursos.forEach(c => {
      categoriasCount[c.categoria] = (categoriasCount[c.categoria] || 0) + c.total_inscritos;
    });
    const dataCategorias = Object.keys(categoriasCount).map(cat => ({ name: cat, value: categoriasCount[cat] }));

    const estadoGlobal = { Completados: 0, "En Progreso": 0 };
    cursos.forEach(c => {
      estadoGlobal.Completados += c.completados;
      estadoGlobal["En Progreso"] += (c.total_inscritos - c.completados);
    });

    const horasHombreMes = documentosCapacitacion.reduce((acc, d) => {
      if (!d?.cursoId || d?.tipoDocumento !== "certificado" || !d?.url) return acc;
      if (!(esDelMesSeleccionado(d.actualizadoEn) || esDelMesSeleccionado(d.generadoEn))) return acc;
      const curso = cursos.find((c) => c.id === d.cursoId);
      return acc + Number(curso?.duracionHoras || 1);
    }, 0);

    const obtenerFecha = (v) => {
      if (!v) return null;
      const f = v?.seconds ? new Date(v.seconds * 1000) : new Date(v);
      return Number.isNaN(f.getTime()) ? null : f;
    };
    const now = new Date();
    const trend = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yy = d.getFullYear();
      const mm = d.getMonth() + 1;
      const key = `${yy}-${String(mm).padStart(2, "0")}`;
      const label = `${String(mm).padStart(2, "0")}/${String(yy).slice(-2)}`;
      const usuariosMes = new Set();
      let horasMes = 0;
      documentosCapacitacion.forEach((doc) => {
        if (doc?.tipoDocumento !== "certificado" || !doc?.url) return;
        const fd = obtenerFecha(doc.actualizadoEn) || obtenerFecha(doc.generadoEn);
        if (!fd) return;
        const docKey = `${fd.getFullYear()}-${String(fd.getMonth() + 1).padStart(2, "0")}`;
        if (docKey !== key) return;
        usuariosMes.add(String(doc.dni || ""));
        const curso = cursos.find((c) => c.id === doc.cursoId);
        horasMes += Number(curso?.duracionHoras || 1);
      });
      trend.push({ mes: label, capacitados: usuariosMes.size, horas: Number(horasMes.toFixed(1)) });
    }

    const asignadosSet = new Set(asignaciones.map((a) => `${a.cursoId}__${a.dni}`));
    const enProgresoSet = new Set();
    const completadosSet = new Set();
    progresos.forEach((p) => {
      if (!Array.isArray(p.modulosCompletados)) return;
      cursos.forEach((c) => {
        const cnt = p.modulosCompletados.filter((m) => String(m).startsWith(`${c.id}_`)).length;
        if (cnt > 0) {
          const key = `${c.id}__${p.dni}`;
          enProgresoSet.add(key);
          if (cnt === (c.modulos?.length || 0) && (c.modulos?.length || 0) > 0) completadosSet.add(key);
        }
      });
    });
    documentosCapacitacion.forEach((d) => {
      if (d?.tipoDocumento === "certificado" && d?.cursoId && d?.dni) {
        completadosSet.add(`${d.cursoId}__${d.dni}`);
      }
    });
    const certificadosSet = new Set(
      documentosCapacitacion
        .filter((d) => d?.tipoDocumento === "certificado" && d?.url && d?.cursoId && d?.dni)
        .map((d) => `${d.cursoId}__${d.dni}`)
    );

    const alertas = [];
    cursos.forEach((c) => {
      if ((c.total_inscritos || 0) >= 3 && (c.engagement || 0) < 40) {
        alertas.push(`Bajo engagement en "${c.titulo}" (${c.engagement.toFixed(1)}%)`);
      }
    });
    if (certificadosSet.size < asignadosSet.size * 0.4 && asignadosSet.size > 0) {
      alertas.push("Baja tasa de certificación respecto a asignaciones.");
    }

    return {
      totalCapacitados: usuariosCapacitadosTotal.size,
      totalCapacitadosMes: usuariosCapacitadosMes.size,
      totalCursos: cursos.length,
      totalHorasHombreMes: horasHombreMes,
      totalInscritos: totalUsuariosEmpresa,
      trend,
      embudo: [
        { etapa: "Asignados", valor: asignadosSet.size },
        { etapa: "En progreso", valor: enProgresoSet.size },
        { etapa: "Completados", valor: completadosSet.size },
        { etapa: "Certificados", valor: certificadosSet.size },
      ],
      alertas: alertas.slice(0, 5),
      dataCategorias,
      dataPie: [
        { name: "Completados", value: estadoGlobal.Completados },
        { name: "En Progreso", value: estadoGlobal["En Progreso"] },
      ],
      topCursos: [...cursos].sort((a, b) => b.engagement - a.engagement).slice(0, 5),
    };
  };

  const stats = calcularEstadisticas();

  if (loading) {
    return (
      <div className="min-vh-100 d-flex justify-content-center align-items-center" style={{ background: "#f8f9fa" }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)", padding: "20px 0" }}>
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        {/* Header Section */}
        <Card className="shadow-lg border-0 mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
          <Card.Header className="text-white py-4 position-relative" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div>
              <h2 className="fw-bold mb-1"><FaChartLine className="me-3" />Dashboard de Capacitación</h2>
              <p className="mb-0 opacity-75">Visualiza el impacto y progreso real de tus programas</p>
            </div>
            <div className="d-flex gap-2">
              <Button variant="light" size="sm" onClick={() => navigate("/capacitaciones/administrar")} className="fw-bold"><FaBook className="me-2" />Administrar Cursos</Button>
              <Button variant="outline-light" size="sm" onClick={cargarDatos}><FaSync /></Button>
            </div>
          </Card.Header>

          <Card.Body className="p-4 bg-light">
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-bold small">Mes de análisis</Form.Label>
                  <Form.Control
                    type="month"
                    value={mesSeleccionado}
                    onChange={(e) => setMesSeleccionado(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            {/* KPI Cards */}
            <Row className="mb-4 g-3">
              <Col md={3}>
                <Card className="border-0 shadow-sm text-white h-100" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", borderRadius: "15px" }}>
                  <Card.Body className="text-center">
                    <FaUserGraduate size={30} className="mb-2" />
                    <h4>{stats.totalCapacitados}</h4>
                    <p className="mb-0 small fw-bold">Total de Capacitados</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm text-white h-100" style={{ background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)", borderRadius: "15px" }}>
                  <Card.Body className="text-center">
                    <FaUserGraduate size={30} className="mb-2" />
                    <h4>{stats.totalCapacitadosMes}</h4>
                    <p className="mb-0 small fw-bold">Capacitados por Mes</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm text-white h-100" style={{ background: "linear-gradient(135deg, #17a2b8 0%, #007bff 100%)", borderRadius: "15px" }}>
                  <Card.Body className="text-center">
                    <FaClock size={30} className="mb-2" />
                    <h4>{stats.totalHorasHombreMes.toFixed(1)}</h4>
                    <p className="mb-0 small fw-bold">Horas Hombre del Mes</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm text-white h-100" style={{ background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)", borderRadius: "15px" }}>
                  <Card.Body className="text-center">
                    <FaUsers size={30} className="mb-2" />
                    <h4>{stats.totalInscritos}</h4>
                    <p className="mb-0 small fw-bold">Total de Usuarios de la Empresa</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Charts Row */}
            <Row className="mb-4">
              <Col lg={8}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white border-bottom-0 pt-3 ps-4"><h5 className="fw-bold mb-0 text-secondary">Engagement por Categoría</h5></Card.Header>
                  <Card.Body>
                    <div style={{ width: "100%", height: 350 }}>
                      <ResponsiveContainer>
                        <BarChart data={stats.dataCategorias}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="value" name="Usuarios Inscritos" fill="#667eea" radius={[10, 10, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white border-bottom-0 pt-3 ps-4"><h5 className="fw-bold mb-0 text-secondary">Estatus de Actividad</h5></Card.Header>
                  <Card.Body className="d-flex align-items-center justify-content-center">
                    <div style={{ width: "100%", height: 350 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie
                            data={stats.dataPie}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            fill="#8884d8"
                            paddingAngle={5}
                            dataKey="value"
                            label
                          >
                            {stats.dataPie.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mb-4 g-4">
              <Col lg={8}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white border-bottom-0 pt-3 ps-4">
                    <h5 className="fw-bold mb-0 text-secondary">Tendencia 12 Meses</h5>
                  </Card.Header>
                  <Card.Body>
                    <div style={{ width: "100%", height: 320 }}>
                      <ResponsiveContainer>
                        <BarChart data={stats.trend}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="mes" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="capacitados" name="Capacitados" fill="#667eea" />
                          <Bar dataKey="horas" name="Horas Hombre" fill="#20c997" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white border-bottom-0 pt-3 ps-4">
                    <h5 className="fw-bold mb-0 text-secondary">Embudo de Capacitación</h5>
                  </Card.Header>
                  <Card.Body>
                    {stats.embudo.map((e, idx) => (
                      <div key={idx} className="mb-3">
                        <div className="d-flex justify-content-between small mb-1">
                          <span>{e.etapa}</span>
                          <span className="fw-bold">{e.valor}</span>
                        </div>
                        <div style={{ height: 8, background: "#eef1f5", borderRadius: 10 }}>
                          <div
                            style={{
                              width: `${stats.embudo[0].valor > 0 ? (e.valor / stats.embudo[0].valor) * 100 : 0}%`,
                              height: "100%",
                              borderRadius: 10,
                              background: "#667eea",
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white border-bottom-0 pt-3 ps-4">
                    <h5 className="fw-bold mb-0 text-secondary">Alertas Accionables</h5>
                  </Card.Header>
                  <Card.Body>
                    {stats.alertas.length === 0 ? (
                      <Alert variant="success" className="mb-0">Sin alertas críticas para el periodo seleccionado.</Alert>
                    ) : (
                      stats.alertas.map((a, i) => (
                        <Alert key={i} variant="warning" className="mb-2">{a}</Alert>
                      ))
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="g-4">
              {/* Top Cursos */}
              <Col lg={6}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white border-bottom-0 pt-3 d-flex justify-content-between">
                    <h5 className="fw-bold mb-0"><FaTrophy className="text-warning me-2" />Ranking de Engagement</h5>
                  </Card.Header>
                  <Card.Body>
                    <Table hover responsive className="mb-0 border-0">
                      <thead className="small text-uppercase text-muted">
                        <tr>
                          <th>Curso</th>
                          <th className="text-center">Eng. %</th>
                          <th className="text-center">Completados</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.topCursos.map((c, i) => (
                          <tr key={i} className="align-middle" style={{ cursor: 'pointer' }} onClick={() => navigate(`/capacitaciones/curso/${c.id}`)}>
                            <td className="fw-bold py-3"><Badge bg="light" text="dark" className="me-2">{i+1}</Badge> {c.titulo}</td>
                            <td className="text-center">
                              <Badge bg={c.engagement > 80 ? "success" : "info"} pill>{c.engagement.toFixed(1)}%</Badge>
                            </td>
                            <td className="text-center fw-bold">{c.completados}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>

              {/* Recent Activity */}
              <Col lg={6}>
                <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px" }}>
                  <Card.Header className="bg-white border-bottom-0 pt-3">
                    <h5 className="fw-bold mb-0"><FaHistory className="text-primary me-2" />Actividad Reciente</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="activity-feed">
                      {respuestas.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          Sin actividad reciente registrada.
                        </div>
                      ) : (
                        respuestas.slice(-5).reverse().map((r, i) => (
                          <div key={i} className="d-flex align-items-center mb-3 p-2 bg-light rounded shadow-sm border-start border-4 border-primary">
                            <div className="avatar me-3">
                               {r.correcta ? <BsCheckCircleFill className="text-success" size={20} /> : <BsClockFill className="text-warning" size={20} />}
                            </div>
                            <div>
                              <p className="mb-0 small"><strong>DNI: {r.dni}</strong> interactuó con el curso ID: <u>{r.cursoId}</u></p>
                              <small className="text-muted">{r.fecha || "Recientemente"}</small>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default DashboardPage;
